import { useCallback, useState, useEffect, useRef } from 'react';

import { usePersonalisationContext } from '../../context/PersonalisationContext';
import { logInfo } from '../../utilities/logging';

export const useViewModel = ({ setCurrentSlotId, currentSlotId }) => {
  const { selections, variants, updateSelections, reorderSlotValues } = usePersonalisationContext();

  // Add ref for the sequence viewer list element.
  const sequentialViewerListRef = useRef(null);

  // Add loader state for auto-advance feedback
  const [isAdvancing, setIsAdvancing] = useState(false);

  // Track if we're in "editing mode" vs "adding mode"
  const [isEditingExistingSlot, setIsEditingExistingSlot] = useState(false);

  const sequentialSlotsTotal = selections.slots.filter((slot) => slot.slotMode === 'SEQUENTIAL');
  logInfo({
    label: 'SequenceViewer',
    info: 'Sequential slots total',
    message: 'Calculated sequential slots total',
    extraData: { sequentialSlotsTotal }
  });
  const sequentialSlotsPopulated = sequentialSlotsTotal.filter((slot) => slot.selected !== null);

  // Calculate populated count for display
  const populatedCount = sequentialSlotsPopulated.length;

  // --- Active slot count logic ---
  const initialActiveCount = Math.max(1, sequentialSlotsPopulated.length);
  const [activeSlotCount, setActiveSlotCount] = useState(initialActiveCount);

  // Ensure activeSlotCount never exceeds the max
  useEffect(() => {
    if (activeSlotCount > sequentialSlotsTotal.length) {
      setActiveSlotCount(sequentialSlotsTotal.length);
    }
  }, [activeSlotCount, sequentialSlotsTotal.length]);

  // If a slot is populated after add, ensure activeSlotCount is at least that many
  useEffect(() => {
    const populatedCount = sequentialSlotsTotal.filter((slot) => slot.selected !== null).length;
    if (populatedCount > activeSlotCount) {
      setActiveSlotCount(populatedCount);
    }
  }, [sequentialSlotsTotal, activeSlotCount]);

  /**
   * Auto-advances to the next available slot after a component is selected.
   * Only auto-advances when adding new items, not when editing existing ones.
   */
  const autoAdvanceToNextSlot = useCallback(() => {
    const nextEmptySlot = sequentialSlotsTotal.find((slot) => slot.selected === null);
    if (nextEmptySlot) {
      // Immediately change to next slot to prevent flash
      setCurrentSlotId(nextEmptySlot.id);

      // Show loader for visual feedback
      setIsAdvancing(true);

      // Hide loader after delay
      setTimeout(() => {
        setIsAdvancing(false);
      }, 300);
    }
  }, [sequentialSlotsTotal, setCurrentSlotId]);

  /**
   * Handles when a user navigates to a specific slot for editing.
   * @param {string} slotId - The slot ID to navigate to.
   */
  const handleSlotSelect = useCallback((slotId) => {
    // Mark that we're editing an existing slot
    setIsEditingExistingSlot(true);
    setCurrentSlotId(slotId);
  }, [setCurrentSlotId]);

  /**
   * Monitors selections changes and auto-advances when a slot is populated.
   * Only auto-advances when NOT editing an existing slot.
   */
  useEffect(() => {
    // Skip auto-advance if we're editing an existing slot
    if (isEditingExistingSlot) {
      return;
    }

    const currentSlot = selections.slots.find((slot) => slot.id === currentSlotId);

    if (currentSlot && currentSlot.selected !== null) {
      const nextEmptySlot = sequentialSlotsTotal.find((slot) => slot.selected === null);
      if (nextEmptySlot) {
        autoAdvanceToNextSlot();
      }
    }
  }, [selections.slots, currentSlotId, sequentialSlotsTotal, autoAdvanceToNextSlot, isEditingExistingSlot]);

  const handleRemove = useCallback(
    (slotId) => {
      updateSelections({ slotId, itemSelected: null });
      // After removal, reorder the slots to compact them (no gaps)
      const newOrder = sequentialSlotsTotal
        .filter((slot) => slot.id !== slotId && slot.selected !== null)
        .map((slot) => slot.id);
      reorderSlotValues({ newOrder });

      // Auto-advance to the next available slot after removal
      autoAdvanceToNextSlot();
    },
    [updateSelections, reorderSlotValues, sequentialSlotsTotal, autoAdvanceToNextSlot]
  );

  // Only show populated slots
  const visibleSlots = sequentialSlotsTotal.filter((slot) => typeof slot.selected === 'string');
  const showAddButton = visibleSlots.length < sequentialSlotsTotal.length;

  // Handler for add button: activate the next available slot
  const handleAddSlot = useCallback(() => {
    // Reset editing mode when manually adding
    setIsEditingExistingSlot(false);
    autoAdvanceToNextSlot();
  }, [autoAdvanceToNextSlot]);

  /**
   * Scrolls the sequence viewer to show the latest charms on the right.
   */
  const handleScrollToEnd = useCallback(() => {
    if (sequentialViewerListRef.current) {
      sequentialViewerListRef.current.scrollLeft = sequentialViewerListRef.current.scrollWidth;
    }
  }, []);

  // Scroll to end whenever visible slots change (new charms added).
  useEffect(() => {
    handleScrollToEnd();
  }, [visibleSlots.length, handleScrollToEnd]);

  return {
    variants,
    visibleSlots,
    showAddButton,
    handleAddSlot,
    handleRemove,
    sequentialSlotsTotal: sequentialSlotsTotal.length,
    sequentialViewerListRef,
    isAdvancing,
    populatedCount,
    handleSlotSelect,
    isEditingExistingSlot
  };
};
